// src/oura/ui/Chips.tsx
//
// The micro visuals: status dot, tag, question-type chip, Google demand chip,
// catalogue family chip, icon square and the kind-of-work icon. Each carries
// a colour so the eye can sort them before reading them (YW, 27 Sep).
//
// API change, 29 Sep 2026 (opt in): WorkKindIcon takes `size?: 14 | 16`
// (default 16, as before); pass 14 inside a `lead="icon"` row.

import { useState, type ReactNode } from "react";
import {
    ActivityIcon,
    ClipboardListIcon,
    FileTextIcon,
    PhoneIcon,
    SearchIcon,
    Share2Icon,
    EyeIcon,
    type LucideIcon,
} from "lucide-react";
import { Tooltip } from "@component/tooltip";
import { cn } from "../lib/cn";
import { MERCHANT_V2 } from "../merchantV2";
import { useOuraRole } from "../role";
import type { VisibilityCheckName } from "../audit";
import { VISIBILITY_CHECK_LABEL } from "../lib/visibilityChecks";
import { searchTier, searchTierLabel } from "../topSearches";
import { AREA_TEXT, DOT_TONE, FAMILY_TONE, ICON_SQUARE_TONE, KIND_TONE, TAG_TONE, type Area, type Family, type Tone } from "./tokens";

/** 8px status dot. Decorative: the status word always sits beside it. */
export function Dot({ tone, className }: { tone: Tone; className?: string }) {
    return <span aria-hidden className={cn("inline-block h-2 w-2 shrink-0 rounded-full", DOT_TONE[tone], className)} />;
}

/** Tinted rounded-full tag, 12px medium. Text only. */
export function Tag({ tone, children, className }: { tone: Tone; children: ReactNode; className?: string }) {
    return (
        <span
            className={cn(
                "inline-flex items-center gap-1.5 whitespace-nowrap rounded-full px-2.5 py-0.5 text-xs font-medium ring-1 ring-inset",
                TAG_TONE[tone],
                className,
            )}
        >
            {children}
        </span>
    );
}

const CHIP = "inline-flex h-5 items-center whitespace-nowrap rounded-md px-2 text-xs font-semibold";

/**
 * What each question type is FOR, in one plain line. The four checks are four
 * different jobs (audit.ts): being found nearby, for a service, at the moment
 * of choosing, and as the trusted name. Shown on hover so a type reads as
 * more than a coloured word.
 */
const KIND_HINT: Record<VisibilityCheckName, string> = {
    location: "Questions people ask when looking for a business like yours in their area.",
    specialty: "Questions about a treatment or service you offer.",
    intent: "Questions from people ready to choose: prices, opening hours, booking.",
    authority: "Questions about who is the most trusted or recommended.",
    // A brand scan's three (v4.5).
    pick: "Questions where people ask which brand to choose.",
    source: "Questions where AI looks for a trusted source to quote.",
    supplier: "Questions about where to buy it, or who supplies it.",
};

/**
 * The question's type, one colour per type, with a tooltip that says what
 * the type is for. `title` adds the backend's own question for the check
 * under it, never restaurant sample copy.
 */
export function KindChip({ kind, title }: { kind: VisibilityCheckName; title?: string }) {
    // Staff, or the flag off: the chip as it was, with the backend's question as its title.
    const role = useOuraRole();
    const merchant = MERCHANT_V2 && role !== "oura-staff";
    if (!merchant) {
        return (
            <span title={title} className={cn(CHIP, KIND_TONE[kind])}>
                {VISIBILITY_CHECK_LABEL[kind]}
            </span>
        );
    }
    return (
        <Tooltip
            asChild
            content={
                <span className="block max-w-[260px]">
                    {KIND_HINT[kind]}
                    {title && <span className="mt-1 block text-gray-300">{title}</span>}
                </span>
            }
        >
            <span className={cn(CHIP, "cursor-help", KIND_TONE[kind])}>{VISIBILITY_CHECK_LABEL[kind]}</span>
        </Tooltip>
    );
}

/**
 * Marks a question the scan asked for the first time. Same shape as the type
 * chip beside it, in the accent blue, so it reads as a second fact about the
 * question and not as a status. `since` is the tooltip: against which scan.
 */
export function NewChip({ since, version }: { since: string; version?: string }) {
    // Always "New": the version the table shows is said once, in its header
    // (Jack, 22 Oct 2026), so the chip only names it on hover.
    return (
        <span
            title={version ? `New in ${version}: not among the questions asked on ${since}` : `Not asked on the previous scan (${since})`}
            className={cn(CHIP, "bg-blue-50 text-blue-700 ring-1 ring-inset ring-blue-200")}
        >
            New
        </span>
    );
}

const RANK_STYLE = {
    top10: { cls: "bg-[#EAF2FF] text-blue-700", bars: 3 },
    top50: { cls: "bg-white text-gray-600 ring-1 ring-inset ring-gray-200", bars: 2 },
    beyond: { cls: "border border-dashed border-gray-300 bg-white font-medium text-gray-500", bars: 1 },
} as const;
const BAR_HEIGHT = ["h-1", "h-[7px]", "h-2.5"] as const;

/**
 * Google demand for a search, with signal bars. Same tiers and the same words
 * as topSearches.ts, so a rank reads identically on every screen. Past 50 is
 * a dashed chip that claims no tier. Renders nothing without a usable rank.
 */
export function RankChip({ rank }: { rank: number | null | undefined }) {
    const tier = searchTier(rank);
    if (!tier || typeof rank !== "number") return null;
    const { cls, bars } = RANK_STYLE[tier];
    const tip = tier === "beyond"
        ? `#${rank} most searched in this market, outside the top 50`
        : `#${rank} most searched in this market, inside the top ${tier === "top10" ? 10 : 50}`;
    return (
        <span title={tip} className={cn(CHIP, "gap-1.5 pl-[7px]", cls)}>
            <span aria-hidden className="inline-flex h-2.5 items-end gap-[1.5px]">
                {BAR_HEIGHT.map((h, i) => (
                    <span key={h} className={cn("block w-[3px] rounded-[1px] bg-current", h, i >= bars && "opacity-25")} />
                ))}
            </span>
            {searchTierLabel(rank)}
        </span>
    );
}

/**
 * The same slot as RankChip when no search reached the question: dashed and
 * grey, so a row without Google demand reads as "none measured" and not as a
 * missing column. `why` is the tooltip.
 */
export function RankChipEmpty({ label, why }: { label: string; why: string }) {
    return (
        <span
            title={why}
            className={cn(CHIP, "border border-dashed border-gray-300 bg-white font-medium text-gray-400")}
        >
            {label}
        </span>
    );
}

/** Catalogue family chip (S, A, B, G), shared with the Act screens. */
/**
 * The standard catalogue's names (aeo_catalogue on the server), so a code chip
 * says what it is on hover: "S7 · Pages load fast". A client's own plan can
 * word a line its own way; this is only the hover, never a label on screen.
 */
const CATALOGUE_NAME: Readonly<Record<string, string>> = {
    B1: "Pages written and published",
    S1: "Existing pages labelled",
    S2: "Google Business Profile, every outlet",
    S3: "Broken pages and dead links fixed",
    S4: "Your name, address and contact corrected across the web",
    S5: "Search Console set up",
    S6: "Pages linked together",
    S7: "Pages load fast",
    A1: "Your 50 questions locked and scored",
    A2: "Business and page facts tagged for AI",
    A3: "Let AI read your site",
    A4: "Handover Scorecard and call",
    G1: "New pages this month",
    G2: "Site health sweep",
    G3: "Your scoreboard",
    G4: "Competitor watch",
    G5: "1 small change",
    G6: "Your monthly snapshot",
    G7: "AI visibility check",
    G8: "Term review",
};

/** "S7 · Pages load fast" for a catalogue code (an outlet's "S2-MK" too); undefined for custom work. */
export function codeName(code: string): string | undefined {
    const base = code.trim().toUpperCase().split("-")[0];
    const name = CATALOGUE_NAME[base];
    return name ? `${code.trim()} · ${name}` : undefined;
}

export function FamilyChip({ family, children, title }: { family: Family; children: ReactNode; title?: string }) {
    // A bare code ("A2") names itself on hover: nobody should need the plan open to read S7.
    const hover = title ?? (typeof children === "string" ? codeName(children) : undefined);
    return <span title={hover} className={cn(CHIP, FAMILY_TONE[family], hover && "cursor-help")}>{children}</span>;
}

/** 32px tinted square holding a 15px glyph: sources by type, files by format. */
export function IconSquare({ tone, icon: Icon, className }: { tone: Tone; icon: LucideIcon; className?: string }) {
    return (
        <span aria-hidden className={cn("grid h-8 w-8 shrink-0 place-items-center rounded-[10px]", ICON_SQUARE_TONE[tone], className)}>
            <Icon size={15} strokeWidth={2} />
        </span>
    );
}

/**
 * A business's own mark in the 32px square: its favicon on white when there is
 * one. While it is absent or fails to load (gone, blocked, not an image), the
 * tinted IconSquare it was given is drawn instead.
 */
export function LogoSquare({ src, tone, icon }: { src: string | null; tone: Tone; icon: LucideIcon }) {
    const [broken, setBroken] = useState<string | null>(null);
    if (!src || broken === src) return <IconSquare tone={tone} icon={icon} />;
    return (
        <span aria-hidden className="grid h-8 w-8 shrink-0 place-items-center overflow-hidden rounded-[10px] bg-white ring-1 ring-inset ring-black/[0.06]">
            <img src={src} alt="" loading="lazy" className="h-5 w-5 object-contain" referrerPolicy="no-referrer" onError={() => setBroken(src)} />
        </span>
    );
}

/**
 * The icon a business's site most likely has: the one its scan found
 * (ourteam-backend#5901), else a guess at the domain's /favicon.ico.
 */
export function faviconOf(faviconUrl: string | null | undefined, domain: string | null | undefined): string | null {
    if (faviconUrl) return faviconUrl;
    const host = (domain ?? "").trim().replace(/^https?:\/\//i, "").replace(/\/.*$/, "");
    return host.includes(".") ? `https://${host}/favicon.ico` : null;
}

const WORK_KIND: Record<string, { icon: LucideIcon; area: Area }> = {
    ai: { icon: EyeIcon, area: "ai" },
    report: { icon: FileTextIcon, area: "google" },
    google: { icon: SearchIcon, area: "google" },
    page: { icon: FileTextIcon, area: "work" },
    site: { icon: ActivityIcon, area: "site" },
    call: { icon: PhoneIcon, area: "people" },
    sent: { icon: Share2Icon, area: "dates" },
};
const WORK_FALLBACK = { icon: ClipboardListIcon, area: "setup" as Area };

/**
 * A row icon coloured by the kind of work. The backend does not send a kind
 * yet (BE handoff "Kind of work on each activity entry"), so an absent or
 * unknown kind is a neutral grey clipboard: never a colour guessed from text.
 * `size` 14 fits a ListRow with `lead="icon"` (a 14px box); 16 is the default.
 */
export function WorkKindIcon({ kind, size = 16 }: { kind?: string | null; size?: 14 | 16 }) {
    const { icon: Icon, area } = (kind && WORK_KIND[kind]) || WORK_FALLBACK;
    return <Icon aria-hidden size={size} strokeWidth={2} className={cn("shrink-0", AREA_TEXT[area])} />;
}
